import type {
  ContentEntry,
  ContentLocalization,
  Draft,
  DraftFrontmatter,
  LlmCategoryResult,
  LlmDescriptionResult,
  LlmGenerateCategoryInput,
  LlmGenerateDescriptionInput,
  LlmGenerateSlugInput,
  LlmGenerateTagsInput,
  LlmProvider,
  LlmProviderCreateInput,
  LlmProviderUpdateInput,
  LlmSlugResult,
  LlmTagsResult,
  LlmVerifyResult,
  Media,
  MediaUploadResult,
  Site,
  SiteUpdate,
} from "@blog/content-schema";

import { ApiError, type ApiErrorBody } from "./api-error";

/**
 * The CMS API client (SPEC §38).
 *
 * The Admin's only way to reach the backend. It holds no credentials, no D1
 * binding and no GitHub token — everything goes through the Worker, which is
 * what keeps the Admin deployable as a plain static bundle (AGENTS.md §3.1).
 *
 * Every method returns a domain type from `@blog/content-schema`, so a field
 * the server renamed fails at compile time here rather than at runtime in a
 * component three levels down.
 */

export interface ApiClientOptions {
  /** Defaults to same-origin, which is how the Admin is served in production. */
  readonly baseUrl?: string;
  /** Injectable for tests. */
  readonly fetch?: typeof fetch;
  /** Called on a 401, so the app can send the user to sign in. */
  readonly onUnauthorized?: () => void;
}

/** Query parameters for the entry list. */
export interface ListEntriesOptions {
  readonly type?: "post" | "page";
  readonly limit?: number;
  readonly offset?: number;
}

/** The GitHub connection status (SPEC §56). */
export interface GitHubStatus {
  readonly github: {
    readonly owner: string;
    readonly repo: string;
    readonly installationId: string;
    readonly webhookConfigured: boolean;
    readonly lastSyncAt: string | null;
    readonly lastActivityAt: string | null;
  };
  readonly content: {
    readonly entries: Record<string, number>;
    readonly localizations: Record<string, number>;
  };
  readonly latestDeployment: Deployment | null;
}

export interface Deployment {
  readonly id: string;
  readonly siteId: string;
  readonly status: "queued" | "building" | "success" | "failed" | "cancelled";
  readonly commitSha?: string;
  readonly url?: string;
  readonly error?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface Discussion {
  readonly id: string;
  readonly siteId: string;
  readonly entryId: string;
  readonly locale: string;
  readonly provider: string;
  readonly discussionId: string;
  readonly discussionNumber: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PublishResult {
  readonly revision: string;
  readonly paths: readonly string[];
  readonly entryJsonPath: string;
  readonly markdownPath: string;
}

export interface SyncResult {
  readonly revision: string;
  readonly entriesCreated: number;
  readonly entriesUpdated: number;
  readonly localizations: number;
  readonly skipped: readonly { path: string; reason: string }[];
}

/** An installed theme, as the theme endpoints report it. */
export interface ThemeInfo {
  readonly id: string;
  readonly version: string;
  readonly name: string;
}

/** A category or tag as the management UI sees it. */
export interface TaxonomyTerm {
  readonly id: string;
  readonly slug: string;
  /** Display name for the site's default locale. */
  readonly name: string;
  /** How many entries reference the term. */
  readonly count: number;
}

/** Which vocabulary a call targets. */
export type TaxonomyKindPath = "categories" | "tags";

/** A site's theme selection. */
export interface ThemeSelection {
  readonly themeId: string;
  readonly themeVersion: string;
  readonly config: Record<string, unknown>;
}

export class CmsApiClient {
  readonly #baseUrl: string;
  readonly #fetch: typeof fetch;
  readonly #onUnauthorized: (() => void) | undefined;

  constructor(options: ApiClientOptions = {}) {
    this.#baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
    // Bound, because `fetch` called as `this.#fetch(...)` would see `this` as
    // the client instance and every browser rejects that ("Illegal
    // invocation") — the app would never load a single byte of data.
    this.#fetch = (options.fetch ?? globalThis.fetch).bind(globalThis);
    this.#onUnauthorized = options.onUnauthorized;
  }

  // ---------------------------------------------------------------- identity

  /** `GET /api/auth/me` — throws on 401, which is how the app learns it is out. */
  async getCurrentUser(): Promise<{ email: string; via: string }> {
    return this.#request("GET", "/api/auth/me");
  }

  /**
   * Rotate the CMS-managed admin credential. The caller must already be
   * authenticated with the current one; the server re-checks it.
   */
  async changePassword(input: { currentPassword: string; newPassword: string }): Promise<void> {
    await this.#request("POST", "/api/auth/password", input);
  }

  // ------------------------------------------------------------------- sites

  async listSites(): Promise<readonly Site[]> {
    const body = await this.#request<{ sites: Site[] }>("GET", "/api/sites");
    return body.sites;
  }

  async getSite(siteId: string): Promise<Site> {
    const body = await this.#request<{ site: Site }>("GET", `/api/sites/${siteId}`);
    return body.site;
  }

  async updateSite(siteId: string, patch: SiteUpdate): Promise<Site> {
    const body = await this.#request<{ site: Site }>("PUT", `/api/sites/${siteId}`, patch);
    return body.site;
  }

  // ----------------------------------------------------------------- entries

  /**
   * Entries plus every localization of them, in one request.
   *
   * The list page needs each entry's title and status, and asking per entry
   * would be N+1 round trips for one screen.
   */
  async listEntries(
    siteId: string,
    options: ListEntriesOptions = {},
  ): Promise<{
    entries: readonly ContentEntry[];
    localizations: readonly ContentLocalization[];
  }> {
    const query = new URLSearchParams();

    if (options.type !== undefined) query.set("type", options.type);
    if (options.limit !== undefined) query.set("limit", String(options.limit));
    if (options.offset !== undefined) query.set("offset", String(options.offset));

    const suffix = query.toString() === "" ? "" : `?${query.toString()}`;

    return this.#request(
      "GET",
      `/api/sites/${siteId}/entries${suffix}`,
    );
  }

  async createEntry(
    siteId: string,
    input: {
      type: "post" | "page";
      canonicalLocale: string;
      contentPath: string;
    },
  ): Promise<ContentEntry> {
    const body = await this.#request<{ entry: ContentEntry }>(
      "POST",
      `/api/sites/${siteId}/entries`,
      input,
    );

    return body.entry;
  }

  async getEntry(siteId: string, entryId: string): Promise<ContentEntry> {
    const body = await this.#request<{ entry: ContentEntry }>(
      "GET",
      `/api/sites/${siteId}/entries/${entryId}`,
    );

    return body.entry;
  }

  async updateEntry(
    siteId: string,
    entryId: string,
    patch: { type?: "post" | "page"; canonicalLocale?: string; contentPath?: string },
  ): Promise<ContentEntry> {
    const body = await this.#request<{ entry: ContentEntry }>(
      "PUT",
      `/api/sites/${siteId}/entries/${entryId}`,
      patch,
    );

    return body.entry;
  }

  async deleteEntry(siteId: string, entryId: string): Promise<void> {
    await this.#request("DELETE", `/api/sites/${siteId}/entries/${entryId}`);
  }

  // ----------------------------------------------------------- localizations

  async listLocalizations(siteId: string, entryId: string): Promise<readonly ContentLocalization[]> {
    const body = await this.#request<{ localizations: ContentLocalization[] }>(
      "GET",
      `/api/sites/${siteId}/entries/${entryId}/localizations`,
    );

    return body.localizations;
  }

  async getLocalization(
    siteId: string,
    entryId: string,
    locale: string,
  ): Promise<ContentLocalization> {
    const body = await this.#request<{ localization: ContentLocalization }>(
      "GET",
      `/api/sites/${siteId}/entries/${entryId}/localizations/${locale}`,
    );

    return body.localization;
  }

  async saveLocalization(
    siteId: string,
    entryId: string,
    locale: string,
    input: {
      title: string;
      slug: string;
      description?: string;
      status: string;
      publishedAt?: string;
      scheduledAt?: string;
      contentPath: string;
    },
    options: { create?: boolean } = {},
  ): Promise<ContentLocalization> {
    const path = `/api/sites/${siteId}/entries/${entryId}/localizations${
      options.create === true ? "" : `/${locale}`
    }`;

    const body = await this.#request<{ localization: ContentLocalization }>(
      options.create === true ? "POST" : "PUT",
      path,
      options.create === true ? { ...input, locale } : input,
    );

    return body.localization;
  }

  async deleteLocalization(siteId: string, entryId: string, locale: string): Promise<void> {
    await this.#request(
      "DELETE",
      `/api/sites/${siteId}/entries/${entryId}/localizations/${locale}`,
    );
  }

  // ------------------------------------------------------------------ drafts

  /** `null` when no draft exists yet, which is the normal state for a new entry. */
  async getDraft(siteId: string, entryId: string, locale: string): Promise<Draft | null> {
    try {
      const body = await this.#request<{ draft: Draft }>(
        "GET",
        `/api/sites/${siteId}/entries/${entryId}/${locale}/draft`,
      );

      return body.draft;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }

      throw error;
    }
  }

  async saveDraft(
    siteId: string,
    entryId: string,
    locale: string,
    input: { content: string; frontmatter: DraftFrontmatter; baseGithubSha?: string },
  ): Promise<Draft> {
    const body = await this.#request<{ draft: Draft }>(
      "PUT",
      `/api/sites/${siteId}/entries/${entryId}/${locale}/draft`,
      input,
    );

    return body.draft;
  }

  // -------------------------------------------------------------- publishing

  async publish(
    siteId: string,
    entryId: string,
    locale: string,
    options: { baseGithubSha?: string } = {},
  ): Promise<PublishResult> {
    const body = await this.#request<{ publish: PublishResult }>(
      "POST",
      `/api/sites/${siteId}/entries/${entryId}/${locale}/publish`,
      options,
    );

    return body.publish;
  }

  async unpublish(
    siteId: string,
    entryId: string,
    locale: string,
  ): Promise<{ revision: string; paths: readonly string[] }> {
    const body = await this.#request<{ unpublish: { revision: string; paths: string[] } }>(
      "POST",
      `/api/sites/${siteId}/entries/${entryId}/${locale}/unpublish`,
    );

    return body.unpublish;
  }

  /** Rendered HTML for the current draft. */
  async preview(
    siteId: string,
    entryId: string,
    locale: string,
  ): Promise<{ html: string; body: string }> {
    const body = await this.#request<{ preview: { html: string; body: string } }>(
      "POST",
      `/api/sites/${siteId}/entries/${entryId}/${locale}/preview`,
    );

    return body.preview;
  }

  // ------------------------------------------------------------------- media

  async listMedia(
    siteId: string,
    options: { search?: string; mimeType?: string; limit?: number } = {},
  ): Promise<readonly Media[]> {
    const query = new URLSearchParams();

    if (options.search !== undefined) query.set("search", options.search);
    if (options.mimeType !== undefined) query.set("mimeType", options.mimeType);
    if (options.limit !== undefined) query.set("limit", String(options.limit));

    const suffix = query.toString() === "" ? "" : `?${query.toString()}`;
    const body = await this.#request<{ media: Media[] }>(
      "GET",
      `/api/sites/${siteId}/media${suffix}`,
    );

    return body.media;
  }

  /**
   * Upload one image.
   *
   * `FormData` rather than JSON because that is what a file input, a drop and a
   * paste all produce — and because the server reads it as multipart.
   */
  async uploadMedia(
    siteId: string,
    entryId: string,
    file: File,
  ): Promise<MediaUploadResult> {
    const form = new FormData();
    form.set("file", file);
    form.set("entryId", entryId);

    const body = await this.#request<{ media: MediaUploadResult }>(
      "POST",
      `/api/sites/${siteId}/media`,
      form,
      // The browser must set the multipart boundary itself.
      { rawBody: true },
    );

    return body.media;
  }

  async deleteMedia(siteId: string, mediaId: string): Promise<void> {
    await this.#request("DELETE", `/api/sites/${siteId}/media/${mediaId}`);
  }

  // ------------------------------------------------------------------ github

  async getGitHubStatus(siteId: string): Promise<GitHubStatus> {
    return this.#request("GET", `/api/sites/${siteId}/github/status`);
  }

  async sync(siteId: string): Promise<SyncResult> {
    const body = await this.#request<{ sync: SyncResult }>(
      "POST",
      `/api/sites/${siteId}/github/sync`,
    );

    return body.sync;
  }

  // ------------------------------------------------------------- deployments

  async listDeployments(siteId: string): Promise<readonly Deployment[]> {
    const body = await this.#request<{ deployments: Deployment[] }>(
      "GET",
      `/api/sites/${siteId}/deployments`,
    );

    return body.deployments;
  }

  // ------------------------------------------------------------------ themes

  async getTheme(siteId: string): Promise<{
    current: ThemeSelection;
    installed: readonly ThemeInfo[];
  }> {
    return this.#request("GET", `/api/sites/${siteId}/theme`);
  }

  async selectTheme(
    siteId: string,
    input: { themeId: string; themeVersion?: string; config?: Record<string, unknown> },
  ): Promise<{ selection: ThemeSelection; revision: string | null }> {
    return this.#request("PUT", `/api/sites/${siteId}/theme`, input);
  }

  // ---------------------------------------------------------------- taxonomy

  async listTaxonomy(siteId: string, kind: TaxonomyKindPath): Promise<readonly TaxonomyTerm[]> {
    const body = await this.#request<{ terms: TaxonomyTerm[] }>(
      "GET",
      `/api/sites/${siteId}/${kind}`,
    );

    return body.terms;
  }

  async createTerm(
    siteId: string,
    kind: TaxonomyKindPath,
    input: { slug: string; name?: string },
  ): Promise<TaxonomyTerm> {
    const body = await this.#request<{ term: TaxonomyTerm }>(
      "POST",
      `/api/sites/${siteId}/${kind}`,
      input,
    );

    return body.term;
  }

  async updateTerm(
    siteId: string,
    kind: TaxonomyKindPath,
    termId: string,
    patch: { slug?: string; name?: string },
  ): Promise<TaxonomyTerm> {
    const body = await this.#request<{ term: TaxonomyTerm }>(
      "PUT",
      `/api/sites/${siteId}/${kind}/${termId}`,
      patch,
    );

    return body.term;
  }

  async deleteTerm(siteId: string, kind: TaxonomyKindPath, termId: string): Promise<void> {
    await this.#request("DELETE", `/api/sites/${siteId}/${kind}/${termId}`);
  }

  // ---------------------------------------------------------------- comments

  async listDiscussions(siteId: string): Promise<readonly Discussion[]> {
    const body = await this.#request<{ discussions: Discussion[] }>(
      "GET",
      `/api/sites/${siteId}/comments`,
    );

    return body.discussions;
  }

  async getComments(
    siteId: string,
    discussionId: string,
  ): Promise<{
    discussion: Discussion;
    comments: readonly { id: string; body: string; author: string; createdAt: string }[];
  }> {
    return this.#request("GET", `/api/sites/${siteId}/comments/${discussionId}`);
  }

  // --------------------------------------------------------------------- llm

  async listLlmProviders(siteId: string): Promise<readonly LlmProvider[]> {
    const body = await this.#request<{ providers: LlmProvider[] }>(
      "GET",
      `/api/sites/${siteId}/llm/providers`,
    );

    return body.providers;
  }

  async createLlmProvider(
    siteId: string,
    input: LlmProviderCreateInput,
  ): Promise<LlmProvider> {
    const body = await this.#request<{ provider: LlmProvider }>(
      "POST",
      `/api/sites/${siteId}/llm/providers`,
      input,
    );

    return body.provider;
  }

  async updateLlmProvider(
    siteId: string,
    providerId: string,
    patch: LlmProviderUpdateInput,
  ): Promise<LlmProvider> {
    const body = await this.#request<{ provider: LlmProvider }>(
      "PUT",
      `/api/sites/${siteId}/llm/providers/${providerId}`,
      patch,
    );

    return body.provider;
  }

  async deleteLlmProvider(siteId: string, providerId: string): Promise<void> {
    await this.#request("DELETE", `/api/sites/${siteId}/llm/providers/${providerId}`);
  }

  /** Probe the endpoint's `/models` with the stored key, server-side. */
  async verifyLlmProvider(siteId: string, providerId: string): Promise<LlmVerifyResult> {
    const body = await this.#request<{ verify: LlmVerifyResult }>(
      "POST",
      `/api/sites/${siteId}/llm/providers/${providerId}/verify`,
    );

    return body.verify;
  }

  /**
   * The provider's live model list, fetched server-side with the stored key.
   *
   * This is what the model picker in the settings form searches over; failures
   * carry the endpoint's own detail so the form can offer a retry.
   */
  async listLlmProviderModels(siteId: string, providerId: string): Promise<readonly string[]> {
    const body = await this.#request<{ models: string[] }>(
      "GET",
      `/api/sites/${siteId}/llm/providers/${providerId}/models`,
    );

    return body.models;
  }

  // ----------------------------------------------------------- llm assistance

  /**
   * AI assistance commands (SPEC §74). They run on the site's default model
   * server-side; the results — slug, tags, category — come back already
   * validated against the same rules a manual save goes through.
   */
  async generateSlug(siteId: string, input: LlmGenerateSlugInput): Promise<LlmSlugResult> {
    return this.#request<LlmSlugResult>(
      "POST",
      `/api/sites/${siteId}/llm/generate-slug`,
      input,
    );
  }

  async generateTags(siteId: string, input: LlmGenerateTagsInput): Promise<LlmTagsResult> {
    return this.#request<LlmTagsResult>(
      "POST",
      `/api/sites/${siteId}/llm/generate-tags`,
      input,
    );
  }

  async generateCategory(
    siteId: string,
    input: LlmGenerateCategoryInput,
  ): Promise<LlmCategoryResult> {
    return this.#request<LlmCategoryResult>(
      "POST",
      `/api/sites/${siteId}/llm/generate-category`,
      input,
    );
  }

  async generateDescription(
    siteId: string,
    input: LlmGenerateDescriptionInput,
  ): Promise<LlmDescriptionResult> {
    return this.#request<LlmDescriptionResult>(
      "POST",
      `/api/sites/${siteId}/llm/generate-description`,
      input,
    );
  }

  // ------------------------------------------------------------------ request

  /**
   * The single place that talks HTTP.
   *
   * Translating the error body into an `ApiError` here means no caller has to
   * know that the server wraps errors in `{ error: { code, message } }`.
   */
  async #request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: { rawBody?: boolean } = {},
  ): Promise<T> {
    const isFormData = options.rawBody === true;

    // In a deployment running the development-auth bypass, the shared secret
    // the user entered at the sign-in prompt travels on every request. Under
    // Cloudflare Access this storage is simply empty and the header is absent
    // — Access authenticates at the edge instead.
    const devSecret =
      typeof localStorage !== "undefined" ? localStorage.getItem("cms-dev-auth-secret") : null;

    const authHeaders: Record<string, string> =
      devSecret !== null && devSecret !== "" ? { "x-dev-auth-secret": devSecret } : {};

    const response = await this.#fetch(`${this.#baseUrl}${path}`, {
      method,
      headers: isFormData
        ? { accept: "application/json", ...authHeaders }
        : { accept: "application/json", "content-type": "application/json", ...authHeaders },
      ...(body === undefined
        ? {}
        : { body: isFormData ? (body as FormData) : JSON.stringify(body) }),
    });

    if (response.status === 401) {
      this.#onUnauthorized?.();
    }

    if (!response.ok) {
      throw await toApiError(response);
    }

    // 204 has no body, and `response.json()` on it throws.
    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }
}

/**
 * Turn a failed response into an `ApiError`.
 *
 * A body that is not the expected shape — a proxy returning an HTML error page,
 * say — still produces a usable `ApiError`, because the alternative is an
 * exception about JSON parsing in a component that only wanted to show a
 * message.
 */
async function toApiError(response: Response): Promise<ApiError> {
  const fallback = new ApiError({
    code: "INTERNAL_ERROR",
    message: "The request failed. Please try again.",
    status: response.status,
  });

  try {
    const body = (await response.json()) as Partial<ApiErrorBody>;

    if (body.error === undefined || typeof body.error.code !== "string") {
      return fallback;
    }

    return new ApiError({
      code: body.error.code,
      message: typeof body.error.message === "string" ? body.error.message : fallback.message,
      status: response.status,
      details: body.error.details,
    });
  } catch {
    return fallback;
  }
}
